import { useState } from 'react';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  getCoreRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';

import { PERFORMANCE_STATUS_SNAKE_CASE_MAP } from './performanceMonitoring/constants';
import { PerformanceStatusOption } from './performanceMonitoring/types';
import { PerformanceStatusFilter } from './PerformanceStatusFilter';
import useCampaignDiagnosticsColumns from '../../hooks/useCampaignDiagnosticsColumns';
import { CampaignDiagnosticsSorting } from '../../schemas';
import { CampaignDiagnosticMetric } from '../../types';

import { useGetStarvationBidderCampaignsQuery } from '@/api/endpoints/starvationBidder';
import usePlatformTraffickingLandingRoute from '@/features/navigation/hooks/usePlatformTraffickingLandingRoute';
import { SearchField } from '@/features/trafficking';
import useFiltersCount from '@/features/trafficking/hooks/useFiltersCount';
import { usePlatform } from '@/features/users';
import { Button } from '@/shared/components/button';
import { Table } from '@/shared/components/table';
import { useGlobalFilterKeys } from '@/shared/hooks';

const CampaignDiagnostics = () => {
  const platform = usePlatform();
  const isDv360 = platform === 'DV360';
  const traffickingLink = usePlatformTraffickingLandingRoute();

  const { starve: _starve, ...searchParams } = useSearch({
    from: '/_app/algorithm-tools/starvation-bidder',
  });
  const navigate = useNavigate({
    from: '/algorithm-tools/starvation-bidder',
  });

  const {
    budgetMin,
    budgetMax,
    algorithmStatus,
    performanceStatus,
    campaignStartDate,
    campaignEndDate,
    ioStartDate,
    ioEndDate,
  } = searchParams;
  const filtersCount = useFiltersCount({
    budgetMin,
    budgetMax,
    algorithmStatus,
    campaignStartDate,
    campaignEndDate,
    ioStartDate,
    ioEndDate,
  });

  const [sorting, setSorting] = useState<SortingState>(() => {
    const { ordering } = searchParams;

    if (!ordering) {
      return [];
    }

    const isDesc = ordering.startsWith('-');
    const fieldName = isDesc ? ordering.substring(1) : ordering;

    return [
      {
        id: fieldName,
        desc: isDesc,
      },
    ];
  });

  const { globalFilterKeys, isGlobalFilterReady } = useGlobalFilterKeys();
  const {
    data: campaignDiagnostics,
    isLoading,
    isFetching,
    isError,
  } = useGetStarvationBidderCampaignsQuery(
    { ...searchParams, ...globalFilterKeys },
    { skip: !isGlobalFilterReady }
  );

  const columns = useCampaignDiagnosticsColumns();
  const table = useReactTable<CampaignDiagnosticMetric>({
    columns,
    data: campaignDiagnostics?.results ?? [],
    getCoreRowModel: getCoreRowModel(),

    state: {
      sorting,
      columnPinning: { left: ['nameAndId', 'algorithmStatus'] },
      pagination: {
        pageIndex: searchParams.page - 1,
        pageSize: searchParams.pageSize,
      },
    },
    enableRowSelection: false,

    manualPagination: true,
    rowCount: campaignDiagnostics?.count ?? 0,
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === 'function'
          ? updater(table.getState().pagination)
          : updater;
      void navigate({
        search: (prev) => ({
          ...prev,
          page: newPagination.pageIndex + 1,
          pageSize: newPagination.pageSize,
        }),
        resetScroll: false,
      });
    },

    manualSorting: true,
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);

      const orderingMap: Record<
        string,
        Exclude<CampaignDiagnosticsSorting, `-${string}`>
      > = {
        campaignName: 'campaign_name',
        yesterdaySpend: 'yesterday_spend',
        totalSpend: 'total_spend',
        neededSpendYesterday: 'needed_spend_yesterday',
        budget: 'budget',
      };

      let ordering: CampaignDiagnosticsSorting = undefined;
      if (newSorting.length > 0) {
        const { id, desc } = newSorting[0];
        ordering = orderingMap[id];
        if (desc && ordering) {
          ordering = `-${ordering}`;
        }
      }

      void navigate({
        search: (prev) => ({
          ...prev,
          ordering,
          page: 1,
        }),
        resetScroll: false,
      });
    },
  });

  const handleSearchChange = (value: string) => {
    const normalizedValue = value || undefined;
    const searchChanged = normalizedValue !== searchParams.search;

    if (!searchChanged) {
      return;
    }

    void navigate({
      search: (prev) => ({
        ...prev,
        search: normalizedValue,
        page: 1,
      }),
      resetScroll: false,
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: (theme) => theme.customShadows.sm,
      }}
    >
      <Stack
        direction='row'
        px={3}
        py={2}
        gap={4}
        alignItems='flex-end'
        justifyContent='space-between'
      >
        <Box>
          <Typography variant='h6' component='h2'>
            Diagnostics
          </Typography>
          <Typography variant='body2' color='text.secondary' pt={0.5}>
            Review key details on algorithm-applied media and whether it&apos;s
            on track to spend budget according to flight timing.
          </Typography>
        </Box>

        <Box>
          <PerformanceStatusFilter
            performanceStatus={performanceStatus}
            onPerformanceStatusChange={(newStatus) => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  page: 1,
                  performanceStatus: newStatus
                    ? PERFORMANCE_STATUS_SNAKE_CASE_MAP[
                        newStatus as PerformanceStatusOption
                      ]
                    : undefined,
                }),
                resetScroll: false,
              });
            }}
          />
        </Box>
      </Stack>

      <Divider />

      <Stack
        px={2}
        py={1.5}
        direction='row'
        gap={2}
        justifyContent='space-between'
        alignItems='center'
      >
        <SearchField
          initialSearchValue={searchParams.search}
          debounceSearchChangeCallback={handleSearchChange}
          textFieldprops={{
            id: 'search-pacing-bidding-metrics-table',
            placeholder: 'Search by name or ID',
          }}
        />

        {filtersCount ? (
          <Button
            size='small'
            onClick={() => {
              void navigate({
                search: (prev) => ({
                  starve: prev.starve,
                  diag: prev.diag,
                  performanceStatus: prev.performanceStatus,
                  page: 1,
                  pageSize: prev.pageSize,
                  search: prev.search,
                  campaignStartDate: undefined,
                  campaignEndDate: undefined,
                }),
                resetScroll: false,
              });
            }}
          >
            Clear filters ({filtersCount})
          </Button>
        ) : null}
      </Stack>

      <Divider />

      <Table
        table={table}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        tableLabel='Pacing Bidding Metrics Table'
        emptyHeading='No diagnostics to show'
        emptyText='Start optimizing using Starvation Bidder to see diagnostics here.'
        errorText='There was an error loading the pacing bidding metrics data. Please try again.'
        ctaText='Go to Trafficking'
        ctaLink={traffickingLink}
        onRowClick={(row) => {
          void navigate({
            search: (prev) => ({
              ...prev,
              diagCampaignOrInsertionId: !isDv360
                ? row.original.campaignId
                : row.original.ioId,
              diagCampaignOrInsertionName: !isDv360
                ? row.original.campaignName
                : row.original.ioName,
              adGroupPage: 1,
              adGroupPageSize: 10,
              adGroupSearch: undefined,
            }),
            resetScroll: false,
          });
        }}
      />
    </Paper>
  );
};

export default CampaignDiagnostics;
